import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { tokenStore } from '../lib/tokenStore';
import { Loader2 } from 'lucide-react';

/**
 * AuthCallbackPage - Handles Google SSO callback
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * 
 * This component processes the session_id from Emergent Auth URL fragment,
 * exchanges it for a JWT token, and redirects to dashboard.
 */
export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          toast.error('Invalid authentication response');
          navigate('/login', { replace: true });
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Exchange session_id for JWT token via our backend
        const response = await api.googleSSOLogin(sessionId);
        const data = response.data;
        
        if (!data.success) {
          const errorMessage = data.message || 'Authentication failed';
          toast.error(errorMessage, { duration: 5000 });
          navigate('/login', { replace: true });
          return;
        }

        // Store token securely
        tokenStore.setToken(data.access_token);
        
        toast.success('Logged in successfully with Google');
        
        // Clear the hash and redirect to dashboard
        window.history.replaceState(null, '', window.location.pathname);
        window.location.href = '/dashboard';
        
      } catch (error) {
        // Determine specific error message
        let message = 'Authentication failed. Please try again.';
        if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.response?.data?.detail) {
          message = error.response.data.detail;
        } else if (error.code === 'ERR_NETWORK') {
          message = 'Network error - please check your connection and try again.';
        } else if (error.code === 'ECONNABORTED') {
          message = 'Request timed out - please try again.';
        }
        
        toast.error(message, { duration: 6000 });
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <p className="text-lg text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
